import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaCalendar } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    appointment_type: 'consultation',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/appointments`, formData, { withCredentials: false });
      toast.success('¡Cita agendada! Te contactaré pronto para confirmar.');
      setFormData({ date: '', time: '', appointment_type: 'consultation', notes: '' });
    } catch (error) {
      toast.error('Error al agendar la cita');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white mb-4">
            <FaCalendar className="text-4xl" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" data-testid="appointments-title">
            Agendar Sesión
          </h1>
          <p className="text-lg text-gray-600">
            Programa una sesión personalizada conmigo
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Solicitud de Cita</CardTitle>
              <CardDescription>Completa el formulario y te contactaré para confirmar</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Fecha Preferida *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                      data-testid="date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time">Hora Preferida *</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      required
                      data-testid="time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Tipo de Sesión *</Label>
                  <select
                    id="type"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.appointment_type}
                    onChange={(e) => setFormData({...formData, appointment_type: e.target.value})}
                    required
                    data-testid="type-select"
                  >
                    <option value="consultation">Consulta Inicial</option>
                    <option value="training">Sesión de Entrenamiento</option>
                    <option value="nutrition">Consulta Nutricional</option>
                    <option value="follow_up">Seguimiento</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Cuéntame qué necesitas o si tienes alguna preferencia..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                    data-testid="notes-input"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="submit-button">
                  {loading ? 'Enviando...' : 'Solicitar Cita'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="mt-8 bg-gradient-to-r from-blue-500 to-purple-500 text-white">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold mb-4">¿Tienes dudas?</h3>
              <p className="mb-4">Contáctame directamente por WhatsApp para una respuesta más rápida</p>
              <Button 
                variant="secondary" 
                onClick={() => {
                  const message = encodeURIComponent('¡Hola Deisy! Quiero agendar una sesión.');
                  window.open(`https://wa.me/573195825712?text=${message}`, '_blank');
                }}
                data-testid="whatsapp-button"
              >
                Contactar por WhatsApp
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Appointments;